import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getMessaging, isSupported, type Messaging } from 'firebase/messaging'

export interface FirebaseWebConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

/** Public Firebase web client config from Vite env (MP Web). Missing keys → null. */
export function getFirebaseWebConfig(): FirebaseWebConfig | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY?.trim() ?? ''
  const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN?.trim() ?? ''
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID?.trim() ?? ''
  const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET?.trim() ?? ''
  const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID?.trim() ?? ''
  const appId = import.meta.env.VITE_FIREBASE_APP_ID?.trim() ?? ''

  if (!apiKey || !authDomain || !projectId || !storageBucket || !messagingSenderId || !appId) {
    return null
  }

  return {
    apiKey,
    authDomain,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
  }
}

export function getFirebaseVapidKey(): string | null {
  const key = import.meta.env.VITE_FIREBASE_VAPID_KEY?.trim() ?? ''
  return key || null
}

let app: FirebaseApp | null = null
let messaging: Messaging | null = null
let messagingInit: Promise<Messaging | null> | null = null

/** Singleton Firebase app for the web client. No-op when env config is incomplete. */
export function getFirebaseApp(): FirebaseApp | null {
  if (typeof window === 'undefined') return null

  const config = getFirebaseWebConfig()
  if (!config) return null

  if (!app) {
    app = getApps().length > 0 ? getApps()[0]! : initializeApp(config)
  }
  return app
}

/**
 * Lazy Messaging instance. Returns null when unsupported, SSR, or config missing.
 * Does not request notification permission.
 */
export async function getFirebaseMessaging(): Promise<Messaging | null> {
  if (typeof window === 'undefined') return null
  if (messaging) return messaging
  if (messagingInit) return messagingInit

  messagingInit = (async () => {
    const firebaseApp = getFirebaseApp()
    if (!firebaseApp) return null

    const supported = await isSupported().catch(() => false)
    if (!supported) return null

    messaging = getMessaging(firebaseApp)
    return messaging
  })()

  return messagingInit
}
